import { Controller } from "@hotwired/stimulus"

// Left-click: append WHERE clause and submit.
// Right-click: context menu with Filter + field-aware Correlate options.
export default class extends Controller {
  #menu = null
  #boundClose = null
  #boundKey = null

  disconnect() {
    this.#destroyMenu()
  }

  filter(event) {
    const cell = event.currentTarget
    const field = cell.dataset.cellFilterField
    const value = cell.textContent.trim()
    if (!field || !value) return
    this.#applyFilter(field, value)
  }

  contextMenu(event) {
    event.preventDefault()
    const cell = event.currentTarget
    const field = cell.dataset.cellFilterField
    const value = cell.textContent.trim()
    if (!field || !value) return
    this.#showMenu(event.clientX, event.clientY, field, value)
  }

  #applyFilter(field, value) {
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return
    const current = textarea.value.trim()
    const clause = `| where ${field} == "${escVal(value)}"`
    textarea.value = current ? `${current}\n${clause}` : clause
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    textarea.closest("form")?.requestSubmit()
    this.#destroyMenu()
  }

  #applyQuery(query) {
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return
    textarea.value = query
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    textarea.closest("form")?.requestSubmit()
    this.#destroyMenu()
  }

  #showMenu(x, y, field, value) {
    this.#destroyMenu()

    const menu = document.createElement("div")
    menu.className = "siem-ctx-menu"
    menu.setAttribute("role", "menu")

    const hint = document.createElement("div")
    hint.className = "siem-ctx-hint"
    hint.textContent = value.length > 44 ? value.slice(0, 41) + "…" : value
    menu.appendChild(hint)

    menu.appendChild(this.#menuItem("fa-filter", "Filter to this value", false, () => {
      this.#applyFilter(field, value)
    }))

    const corr = CORRELATIONS[field]
    if (corr) {
      const sep = document.createElement("div")
      sep.className = "siem-ctx-sep"
      menu.appendChild(sep)
      menu.appendChild(this.#menuItem(corr.icon, corr.label, true, () => {
        this.#applyQuery(corr.query(value))
      }))
    }

    menu.style.visibility = "hidden"
    document.body.appendChild(menu)
    this.#menu = menu

    const mw = menu.offsetWidth
    const mh = menu.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    menu.style.left = (x + mw > vw - 8 ? x - mw : x) + "px"
    menu.style.top  = (y + mh > vh - 8 ? y - mh : y) + "px"
    menu.style.visibility = ""

    this.#boundClose = e => { if (!menu.contains(e.target)) this.#destroyMenu() }
    this.#boundKey   = e => { if (e.key === "Escape") this.#destroyMenu() }
    setTimeout(() => document.addEventListener("click", this.#boundClose), 0)
    document.addEventListener("keydown", this.#boundKey)
  }

  #menuItem(icon, label, isCorrelate, onClick) {
    const btn = document.createElement("button")
    btn.className = "siem-ctx-item" + (isCorrelate ? " siem-ctx-item--correlate" : "")
    btn.type = "button"
    btn.innerHTML = `<i class="fa ${icon}"></i> ${escHtml(label)}`
    btn.addEventListener("click", onClick)
    return btn
  }

  #destroyMenu() {
    this.#menu?.remove()
    this.#menu = null
    if (this.#boundClose) {
      document.removeEventListener("click", this.#boundClose)
      this.#boundClose = null
    }
    if (this.#boundKey) {
      document.removeEventListener("keydown", this.#boundKey)
      this.#boundKey = null
    }
  }
}

function escVal(str) {
  return String(str).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
}

const SESSION_QUERY = v =>
  `FROM logs | WHERE TargetLogonId == "${escVal(v)}" OR SubjectLogonId == "${escVal(v)}" OR LinkedLogonId == "${escVal(v)}" | SORT TimeCreated ASC`

const CORRELATIONS = {
  TargetLogonId:  { label: "Correlate session", icon: "fa-link", query: SESSION_QUERY },
  SubjectLogonId: { label: "Correlate session", icon: "fa-link", query: SESSION_QUERY },
  LinkedLogonId:  { label: "Correlate session", icon: "fa-link", query: SESSION_QUERY },
  ProcessGuid: {
    label: "Correlate process tree",
    icon: "fa-sitemap",
    query: v => `FROM logs | WHERE ProcessGuid == "${escVal(v)}" | SORT TimeCreated ASC`,
  },
  Hashes: {
    label: "Correlate binary across hosts",
    icon: "fa-database",
    query: v => `FROM logs | WHERE Hashes LIKE "%${escVal(v)}%" | STATS count = COUNT(*), computers = COUNT_DISTINCT(Computer), users = COUNT_DISTINCT(SubjectUserName) BY EventID, ProcessName | SORT count DESC`,
  },
  IpAddress: {
    label: "Correlate source IP",
    icon: "fa-globe",
    query: v => `FROM logs | WHERE IpAddress == "${escVal(v)}" | SORT TimeCreated ASC`,
  },
  Computer: {
    label: "Correlate host activity",
    icon: "fa-desktop",
    query: v => `FROM logs | WHERE Computer == "${escVal(v)}" | SORT TimeCreated ASC`,
  },
  SubjectUserName: {
    label: "Correlate user activity",
    icon: "fa-user",
    query: v => `FROM logs | WHERE SubjectUserName == "${escVal(v)}" OR TargetUserName == "${escVal(v)}" | SORT TimeCreated ASC`,
  },
  TargetUserName: {
    label: "Correlate user activity",
    icon: "fa-user",
    query: v => `FROM logs | WHERE SubjectUserName == "${escVal(v)}" OR TargetUserName == "${escVal(v)}" | SORT TimeCreated ASC`,
  },
}
